import * as React from 'react';
import moment from 'moment';
import { ActivityAction, ActivityResourceName } from '@pec/aion-ui-core/interfaces/activities';
import { Alert, AlertTitle } from '@material-ui/lab';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { IMainSubscription } from '@pec/aion-ui-core/interfaces/mainSubscription';
import { localizeDate } from '@pec/aion-ui-i18next/helpers/localize';
import { NavLink } from 'react-router-dom';
import { SubscriptionTiers } from '@pec/aion-ui-core/enums/Subscription';
import { Trans, useTranslation } from 'react-i18next';
import { LoadingOverlay } from './LoadingOverlay';
import { usePermission } from '../hooks/usePermission';
import { CommonRootState } from '@pec/aion-ui-core/combineReducers';
import { useSelector } from 'react-redux';
import { AppName } from '@pec/aion-ui-core/interfaces/appName';
import { getRenewalAvailableInDays } from '@pec/aion-ui-core/constants/subscription';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    infoLink: {
      fontWeight: theme.typography.fontWeightBold,
      color: 'var(--text-error)'
    },
    errorLink: {
      fontWeight: theme.typography.fontWeightMedium,
      color: 'var(--text-error)'
    }
  })
);

type Props = {
  organizationId: string;
  subscription: IMainSubscription;
};

const renewSessionKeys = [
  'survey:formState_organizationId_Renewal',
  'survey:orderPageSelectedProduct_organizationId_Renewal'
];

export const SubscriptionAlert: React.FC<Props> = ({ organizationId, subscription }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { userInfo } = useSelector((state: CommonRootState) => state.userInfo);
  const { organizationInfo, isFetching } = useSelector((state: CommonRootState) => state.organizationInfo);
  const { hasGlobalPermission, hasOrganizationOnlyPermission } = usePermission();
  const hasSubscriptionsPermission =
    hasGlobalPermission(ActivityAction.Write, ActivityResourceName.Subscriptions) ||
    hasOrganizationOnlyPermission(ActivityAction.Write, ActivityResourceName.Subscriptions);

  const { countryISO2, description, expirationDate } = subscription;
  const usContractor = countryISO2 === 'US';
  const chasContractor = ['GB', 'AU'].includes(countryISO2);
  const isFoundation = description === SubscriptionTiers.Foundation;
  const isExpiring = expirationDate
    ? moment(expirationDate).isBetween(moment(), moment().add(usContractor ? 30 : getRenewalAvailableInDays(), 'days'))
    : false;
  const isExpired = expirationDate ? moment(expirationDate).isBefore() : false;
  const expirationDateString = expirationDate
    ? localizeDate(moment(`${moment(expirationDate).format('YYYY-MM-DD')}T00:00:00`).toISOString(), t)
    : '';
  const isChild = organizationInfo?.isChild ?? false;
  const isParent = organizationInfo?.isParent ?? false;
  const isGroupChasContractor = chasContractor && (isChild || isParent);

  if ((!isExpiring && !isExpired) || isFoundation) return null;

  let title: string = '';
  let text: string | JSX.Element = '';

  const clearOperationStoredData = () => {
    renewSessionKeys.forEach(key => {
      sessionStorage.removeItem(key.replace('organizationId', organizationId));
    });
  };

  if (isExpiring && usContractor) {
    title = t('components.subscriptionAlert.usExpiringTitle', {
      defaultValue: 'Your {{description}} subscription will expire soon.',
      description
    });
    text = (
      <Trans i18nKey="components.subscriptionAlert.usExpiringText">
        {{ description }} expires on {{ expirationDateString }}. Please call
        <a href="tel:844-768-9041" className={classes.infoLink}>
          844-768-9041
        </a>
        to renew.
      </Trans>
    );
  }
  if ((isExpiring || isExpired) && chasContractor) {
    title = !isExpired
      ? t('components.subscriptionAlert.chasExpiringTitle', {
          defaultValue: 'Your {{description}} membership will expire soon.',
          description
        })
      : (title = t('components.subscriptionAlert.chasExpiredTitle', {
          defaultValue: 'Your {{description}} membership has expired - {{firstName}}: it’s time to renew.',
          description,
          firstName: userInfo?.firstName ? `, ${userInfo.firstName}` : ''
        }));
    text = hasSubscriptionsPermission ? (
      <>
        {process.env.REACT_APP_NAME === AppName.AionSubscriberUI ? (
          <Trans i18nKey="components.subscriptionAlert.chasExpiringAdminText">
            If you do not renew, you'll lose access to your membership benefits and you will also be unable to maintain
            your certified status. Take action now - renew your membership and your certification.
            <NavLink to={`/${organizationId}/renew`} className={classes.infoLink} onClick={clearOperationStoredData}>
              Renew now.
            </NavLink>
          </Trans>
        ) : (
          <Trans i18nKey="components.subscriptionAlert.chasExpiringAdminText">
            If you do not renew, you'll lose access to your membership benefits and you will also be unable to maintain
            your certified status. Take action now - renew your membership and your certification.
            <a
              href={`/organizations/${organizationId}/renew`}
              className={classes.infoLink}
              onClick={clearOperationStoredData}
            >
              Renew now.
            </a>
          </Trans>
        )}
      </>
    ) : (
      t(
        'components.subscriptionAlert.chasExpiringNonAdminText',
        "Please contact your company's administrator to renew your membership today. If you do not renew, you'll lose access to your membership benefits and you will also be unable to maintain your certified status."
      )
    );
  }

  if ((isExpiring || isExpired) && isGroupChasContractor) {
    title = isExpired
      ? t('components.subscriptionAlert.groupExpiredTitle', {
          defaultValue: 'Your {{description}} membership has expired.',
          description
        })
      : t('components.subscriptionAlert.chasExpiringTitle', {
          defaultValue: 'Your {{description}} membership will expire soon.',
          description
        });
    text = isExpired ? (
      <Trans i18nKey="components.subscriptionAlert.groupExpiredText">
        {{ description }} membership expired on {{ expirationDateString }}. Please call
        <a href="tel:03455219222" className={classes.errorLink}>
          0345 521 9222
        </a>
        to renew. If you do not renew, you’ll lose access to your membership benefits and you will also be unable to
        maintain your certified status.
      </Trans>
    ) : (
      <Trans i18nKey="components.subscriptionAlert.groupExpiringText">
        {{ description }} membership is expiring on {{ expirationDateString }}. Please call
        <a href="tel:03455219222" className={classes.infoLink}>
          0345 521 9222
        </a>
        to renew. If you do not renew, you’ll lose access to your membership benefits and you will also be unable to
        maintain your certified status.
      </Trans>
    );
  }

  if (isExpired && usContractor) {
    title = t('components.subscriptionAlert.usExpiredTitle', {
      defaultValue: 'Your {{description}} subscription has expired.',
      description
    });
    text = (
      <Trans i18nKey="components.subscriptionAlert.usExpiredText">
        {{ description }} expired on {{ expirationDateString }}. Please call
        <a href="tel:844-768-9041" className={classes.infoLink}>
          844-768-9041
        </a>
        to renew.
      </Trans>
    );
  }

  if (title !== '') {
    return (
      <LoadingOverlay isLoading={isFetching}>
        <Alert severity={isExpired ? 'error' : 'info'}>
          <AlertTitle>{title}</AlertTitle>
          <span>{text}</span>
        </Alert>
      </LoadingOverlay>
    );
  }

  return null;
};
